type TodoAssignee = 'manager' | 'liver'

export interface InsightCall {
  id: string
  called_at: string
  todos: Array<{ task: string; assignee: TodoAssignee; completed?: boolean }> | null
  next_follow_date: string | null
}

export interface TodoInsight {
  callId: string
  calledAt: string
  task: string
  assignee: TodoAssignee
}

export type NextFollowState = 'today' | 'upcoming' | 'overdue'

export interface NextFollowInsight {
  callId: string
  date: string
  state: NextFollowState
}

export function getRecentTodos(calls: InsightCall[], limit = 5): TodoInsight[] {
  return [...calls]
    .sort((a, b) => new Date(b.called_at).getTime() - new Date(a.called_at).getTime())
    .flatMap((call) =>
      (call.todos ?? [])
        .filter((todo) => todo.task.trim().length > 0)
        .filter((todo) => todo.completed !== true)
        .map((todo) => ({
          callId: call.id,
          calledAt: call.called_at,
          task: todo.task,
          assignee: todo.assignee
        }))
    )
    .slice(0, limit)
}

export function getNextFollow(
  calls: InsightCall[],
  now = new Date()
): NextFollowInsight | null {
  const today = startOfLocalDay(now).getTime()
  const candidates = calls
    .filter((call) => call.next_follow_date)
    .map((call) => ({
      callId: call.id,
      date: call.next_follow_date as string,
      time: parseLocalDate(call.next_follow_date as string).getTime()
    }))
    .filter((candidate) => Number.isFinite(candidate.time))
    .sort((a, b) => a.time - b.time)

  const upcoming = candidates.find((candidate) => candidate.time >= today)
  if (upcoming) {
    return {
      callId: upcoming.callId,
      date: upcoming.date,
      state: upcoming.time === today ? 'today' : 'upcoming'
    }
  }

  const overdue = [...candidates].reverse().find((candidate) => candidate.time < today)
  if (!overdue) return null
  return {
    callId: overdue.callId,
    date: overdue.date,
    state: 'overdue'
  }
}

function parseLocalDate(date: string): Date {
  return new Date(`${date}T00:00:00`)
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}
